export interface Logger {
	/** Log a message only intended for debugging when an error occurs */
	debug: (message: string) => void;

	/** Log a message that can be checked to see normal operation of the software */
	info: (message: string) => void;

	/** Log a message that indicates something is suspicious but recoverable */
	warning: (message: string) => void;

	/** Log a message indicating something blew up */
	error: (message: string, error?: Error) => void;
}

let handlers: Logger[] = [];

export function setHandlers(newHandlers: Logger[]) {
	handlers = newHandlers;
}

/**
 * Get a logger with the given name.
 * All lines logged through this logger will be marked with the given name.  Message will be logged through all
 * configured handlers (see #setHandlers)
 */
export function getLogger(name: string): Logger {
	return {
		debug: (msg) => {
			for (const handler of handlers) handler.debug(`[${name}] ${msg}`);
		},
		info: (msg) => {
			for (const handler of handlers) handler.info(`[${name}] ${msg}`);
		},
		warning: (msg) => {
			for (const handler of handlers) handler.warning(`[${name}] ${msg}`);
		},
		error: (msg, error?) => {
			for (const handler of handlers) handler.error(`[${name}] ${msg}`, error);
		},
	};
}

// istanbul ignore next: testing this would only work by patching out a system-level object.  considering the
// implementation is trivial, it's not worth the cost.
export class ConsoleLogger implements Logger {
	public debug(message: string) {
		// eslint-disable-next-line no-console
		console.debug(message);
	}

	public info(message: string) {
		// eslint-disable-next-line no-console
		console.info(message);
	}

	public warning(message: string) {
		// eslint-disable-next-line no-console
		console.warn(message);
	}

	public error(message: string, error?: Error) {
		console.error(error ? `${message} - ${error.message}\n${error.stack}` : message);
	}
}

// Set default handlers
setHandlers([new ConsoleLogger()]);
